<template>
  <div class="container mt-4 mb-5">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h3 class="m-0">Vorlagenverwaltung</h3>
      <router-link class="btn btn-outline-secondary btn-sm" :to="{ name: 'Invoices' }">
        Zurück zur Rechnungsübersicht
      </router-link>
    </div>

    <div class="card mb-4">
      <div class="card-body">
        <h5 class="card-title">Neue Vorlage hochladen</h5>
        <p class="text-muted mb-3">
          Unterstützte Formate: <code>.odt</code>, <code>.ott</code>, <code>.xlsx</code>
        </p>
        <div class="row g-2 align-items-end">
          <div class="col-md-8">
            <label class="form-label" for="templateUpload">Datei</label>
            <input
              id="templateUpload"
              type="file"
              class="form-control"
              accept=".odt,.ott,.xlsx"
              @change="onFileSelected"
            />
          </div>
          <div class="col-md-4">
            <button
              class="btn btn-primary w-100"
              :disabled="!selectedFile || !canWriteFiles"
              @click="uploadTemplate"
            >
              Hochladen
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="card-title m-0">Vorlagen</h5>
          <div class="d-flex align-items-center gap-2">
            <label class="form-label m-0" for="typeFilter">Typ</label>
            <select id="typeFilter" class="form-select form-select-sm" v-model="typeFilter" @change="loadTemplates">
              <option value="all">Alle</option>
              <option value="openoffice">OpenOffice</option>
              <option value="excel">Excel</option>
            </select>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table table-striped table-hover">
            <thead>
              <tr>
                <th>Dateiname</th>
                <th>Dateityp</th>
                <th>Erstellt</th>
                <th class="text-end">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="template in templates" :key="template.id">
                <td>
                  {{ template.originalFileName }}
                  <small v-if="template.sourceFileId" class="d-block text-muted">Neue Vorlagenversion</small>
                </td>
                <td>{{ template.fileType }}</td>
                <td>{{ formatDateTime(template.creationTimestamp) }}</td>
                <td class="text-end">
                  <button class="btn btn-sm btn-outline-primary me-2" @click="downloadTemplate(template)">
                    Download
                  </button>
                  <button v-if="canManageTemplates" class="btn btn-sm btn-outline-primary me-2" @click="openAction(template, 'rename')">Umbenennen</button>
                  <button v-if="canManageTemplates" class="btn btn-sm btn-outline-secondary me-2" @click="openAction(template, 'duplicate')">Duplizieren</button>
                  <button v-if="canManageTemplates" class="btn btn-sm btn-outline-secondary me-2" @click="openAction(template, 'version')">Neue Version</button>
                  <button class="btn btn-sm btn-outline-danger" :disabled="!canDeleteFiles" @click="deleteTemplate(template)">
                    Löschen
                  </button>
                </td>
              </tr>
              <tr v-if="templates.length === 0">
                <td colspan="4" class="text-center text-muted py-4">Keine Vorlagen gefunden.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <div v-if="actionTemplate" class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true" aria-labelledby="templateActionTitle">
      <div class="modal-dialog"><form class="modal-content" @submit.prevent="saveAction">
        <div class="modal-header">
          <h5 id="templateActionTitle" class="modal-title">{{ actionTitle }}</h5>
          <button type="button" class="btn-close" :disabled="savingAction" aria-label="Schließen" @click="closeAction"></button>
        </div>
        <div class="modal-body">
          <template v-if="action === 'version'">
            <p>Die bisherige Version bleibt für vorhandene Dokumente erhalten. Die neue Version kann anschließend für neue Dokumente ausgewählt werden.</p>
            <label class="form-label" for="templateVersionFile">Neue Datei im gleichen Vorlagenformat</label>
            <input id="templateVersionFile" class="form-control" type="file" :accept="versionExtension" required @change="versionFile = $event.target.files?.[0] || null" />
          </template>
          <template v-else>
            <label class="form-label" for="templateActionName">Dateiname</label>
            <input id="templateActionName" v-model="actionName" class="form-control" maxlength="200" required />
            <p v-if="action === 'duplicate'" class="text-muted mt-2">Die Kopie erhält eine eigene Datei. Die ursprüngliche Vorlage bleibt unverändert.</p>
          </template>
          <p v-if="actionError" class="alert alert-danger mt-3" role="alert">{{ actionError }}</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" :disabled="savingAction" @click="closeAction">Abbrechen</button>
          <button type="submit" class="btn btn-primary" :disabled="savingAction || (action === 'version' ? !versionFile : !actionName.trim())">Speichern</button>
        </div>
      </form></div>
    </div>
    <div v-if="actionTemplate" class="modal-backdrop fade show"></div>
  </div>
</template>

<script>
import FileTemplateService from "@/services/FileTemplateService";
import { useUserStore } from "@/stores/userStore";

const disableAuthForE2E = process.env.VUE_APP_DISABLE_AUTH === "true";

export default {
  name: "TemplateAdminView",
  data() {
    return {
      templates: [],
      selectedFile: null,
      typeFilter: "all",
      userStore: useUserStore(),
      actionTemplate: null,
      action: "",
      actionName: "",
      actionError: "",
      versionFile: null,
      savingAction: false,
    };
  },
  computed: {
    canWriteFiles() {
      if (disableAuthForE2E) return true;
      return this.userStore.hasPermission("files.write");
    },
    canDeleteFiles() {
      if (disableAuthForE2E) return true;
      return this.userStore.hasPermission("files.delete");
    },
    canManageTemplates() {
      return this.canWriteFiles && (disableAuthForE2E || this.userStore.hasPermission("files.read"));
    },
    actionTitle() {
      return { rename: "Vorlage umbenennen", duplicate: "Vorlage duplizieren", version: "Neue Vorlagenversion" }[this.action] || "Vorlage bearbeiten";
    },
    versionExtension() {
      return `.${String(this.actionTemplate?.fileType || "").toLowerCase()}`;
    },
  },
  methods: {
    openAction(template, action) {
      if (!this.canManageTemplates) return;
      this.actionTemplate = template;
      this.action = action;
      this.actionError = "";
      this.versionFile = null;
      this.actionName = action === "duplicate"
        ? template.originalFileName.replace(/(\.[^.]+)$/, " - Kopie$1")
        : template.originalFileName;
    },
    closeAction() {
      if (!this.savingAction) this.actionTemplate = null;
    },
    async saveAction() {
      if (!this.canManageTemplates || !this.actionTemplate || this.savingAction) return;
      this.savingAction = true;
      this.actionError = "";
      try {
        const id = this.actionTemplate.id;
        if (this.action === "version") await FileTemplateService.createTemplateVersion(id, this.versionFile);
        else if (this.action === "duplicate") await FileTemplateService.duplicateTemplate(id, this.actionName.trim());
        else await FileTemplateService.renameTemplate(id, this.actionName.trim());
        this.actionTemplate = null;
        await this.loadTemplates();
      } catch (error) {
        this.actionError = typeof error?.response?.data === "string"
          ? error.response.data : "Die Vorlage konnte nicht gespeichert werden. Bitte prüfen Sie Dateiname, Format und Berechtigungen.";
      } finally {
        this.savingAction = false;
      }
    },
    getFileTypesForCurrentFilter() {
      switch (this.typeFilter) {
        case "openoffice":
          return FileTemplateService.OPENOFFICE_FILE_TYPES;
        case "excel":
          return FileTemplateService.EXCEL_FILE_TYPES;
        default:
          return [
            ...FileTemplateService.OPENOFFICE_FILE_TYPES,
            ...FileTemplateService.EXCEL_FILE_TYPES,
          ];
      }
    },
    async loadTemplates() {
      try {
        const response = await FileTemplateService.getInvoiceTemplates(this.getFileTypesForCurrentFilter());
        const templates = Array.isArray(response?.data) ? response.data : [];
        this.templates = templates;
      } catch (error) {
        console.error("Failed to load templates:", error);
      }
    },
    onFileSelected(event) {
      this.selectedFile = event?.target?.files?.[0] || null;
    },
    async uploadTemplate() {
      if (!this.selectedFile) {
        return;
      }

      try {
        await FileTemplateService.uploadTemplate(this.selectedFile);
        this.selectedFile = null;
        const input = document.getElementById("templateUpload");
        if (input) {
          input.value = "";
        }
        await this.loadTemplates();
      } catch (error) {
        console.error("Template upload failed:", error);
      }
    },
    async downloadTemplate(template) {
      try {
        await FileTemplateService.downloadFile(template.id, template.originalFileName || "template");
      } catch (error) {
        console.error("Template download failed:", error);
      }
    },
    async deleteTemplate(template) {
      const confirmed = window.confirm(`Vorlage "${template.originalFileName}" wirklich löschen?`);
      if (!confirmed) {
        return;
      }

      try {
        await FileTemplateService.deleteTemplate(template.id);
        await this.loadTemplates();
      } catch (error) {
        console.error("Template deletion failed:", error);
      }
    },
    formatDateTime(value) {
      if (!value) {
        return "-";
      }

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return "-";
      }

      return date.toLocaleString();
    },
  },
  mounted() {
    this.loadTemplates();
  },
};
</script>

